import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack
} from '@chakra-ui/react'
import {
  FaChevronLeft,
  FaChevronRight,
  FaFileExcel,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrash
} from 'react-icons/fa'
import { getPartnerIbans } from '../../api/getPartnerIbans'
import { exportPartnerIbansRequest } from '../../api/exportPartnerIbans'
import { getPartners, getOrganisations } from '../../../MainPage/api/getNomenclatures'
import { useDebounce } from '../../../../hooks'
import FilterPopoverWithCheck from '../../../MainPage/components/DocumentTable/FilterPopoverWithCheck'
import FilterMultiSelectPopover from '../../../MainPage/components/DocumentTable/FilterMultiSelectPopover'
import { LABEL } from '../../../../constants'
import getWCRShortName from '../../../../helpers/wcrCompany'

const PartnerIbanTable = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalPages, setTotalPages] = useState(1)
  const [availablePartnerGroups, setAvailablePartnerGroups] = useState([])
  const [organisations, setOrganisations] = useState([])
  const [isExporting, setIsExporting] = useState(false)
  const toast = useToast()

  const [searchTerm, setSearchTerm] = useState(
    () => new URLSearchParams(window.location.search).get('searchTerm') ?? ''
  )
  const [selectedPartners, setSelectedPartners] = useState([])
  const [partnerGroupFilter, setPartnerGroupFilter] = useState([])
  const [companyFilter, setCompanyFilter] = useState([])

  const [pagination, setPagination] = useState(() => {
    const url = new URLSearchParams(window.location.search)
    const pageNumber = parseInt(url.get('page'), 10) || 0
    const size = parseInt(url.get('size'), 10) || 20
    let orderByList = [{ field: 'name', direction: 'ASCENDING' }]
    if (url.get('sort')) {
      const [field, direction] = url.get('sort').split(',')
      orderByList = [{ field, direction }]
    }
    return { pageNumber, size, orderByList }
  })
  const [pageInput, setPageInput] = useState(pagination.pageNumber + 1)

  const debouncedSearch = useDebounce(searchTerm, 300)

  const partnerCifs = useMemo(
    () => selectedPartners.map((p) => p.cif).filter((c) => c !== 'null'),
    [selectedPartners]
  )

  const worldClassCifs = useMemo(
    () =>
      companyFilter.length > 0
        ? organisations.filter((o) => companyFilter.includes(o.name)).map((o) => o.cif)
        : null,
    [companyFilter, organisations]
  )

  useEffect(() => {
    setPageInput(pagination.pageNumber + 1)
  }, [pagination.pageNumber])

  useEffect(() => {
    getPartners({ pageNumber: 0, size: 500, orderByList: [{ field: 'name', direction: 'ASCENDING' }] })
      .then((response) => {
        if (response?.content) {
          const groups = [
            ...new Set(
              response.content
                .map((p) => p.partnerGroup)
                .filter(Boolean)
            )
          ].sort()
          setAvailablePartnerGroups(groups)
        }
      })
      .catch((err) => console.error('Error loading partner groups:', err))
  }, [])

  useEffect(() => {
    getOrganisations()
      .then((orgs) => { if (Array.isArray(orgs)) setOrganisations(orgs) })
      .catch((err) => console.error('Error loading organisations:', err))
  }, [])

  const getSortIcon = (field) => {
    const sortField = pagination.orderByList[0]?.field
    const sortDir = pagination.orderByList[0]?.direction
    if (sortField === field)
      return sortDir === 'ASCENDING' ? <FaSortUp /> : <FaSortDown />
    return <FaSort />
  }

  const handleSort = (field) => {
    setPagination((prev) => {
      const existing = prev.orderByList.find((o) => o.field === field)
      const newDir =
        existing && existing.direction === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING'
      return { ...prev, orderByList: [{ field, direction: newDir }] }
    })
  }

  const goToPage = () => {
    const parsed = parseInt(pageInput, 10)
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      setPagination((prev) => ({ ...prev, pageNumber: parsed - 1 }))
    } else {
      toast({
        title: 'Număr de pagină invalid',
        description: `Introduceți un număr între 1 și ${totalPages}.`,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const { pageNumber, size, orderByList } = pagination
      const response = await getPartnerIbans({
        partnerCifs: partnerCifs.length > 0 ? partnerCifs : null,
        partnerGroups: partnerGroupFilter.length > 0 ? partnerGroupFilter : null,
        worldClassCifs: worldClassCifs,
        searchTerm: searchTerm || null,
        pageNumber,
        size,
        orderByList
      })
      if (response?.content) {
        setRows(response.content)
        setTotalPages(Math.ceil((response.total || 0) / size))
      } else {
        setRows([])
        setTotalPages(1)
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      console.error('PartnerIbanTable fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await exportPartnerIbansRequest({
        partnerCifs: partnerCifs.length > 0 ? partnerCifs : null,
        partnerGroups: partnerGroupFilter.length > 0 ? partnerGroupFilter : null,
        worldClassCifs: worldClassCifs,
        searchTerm: searchTerm || null
      })
    } catch (error) {
      toast({
        title: 'Eroare export',
        description: 'Nu s-a putut genera fișierul Excel.',
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const updateUrlParams = () => {
    const urlParams = new URLSearchParams()
    urlParams.set('page', pagination.pageNumber)
    urlParams.set('size', pagination.size)
    if (pagination.orderByList.length > 0) {
      const { field, direction } = pagination.orderByList[0]
      urlParams.set('sort', `${field},${direction}`)
    }
    if (searchTerm) urlParams.set('searchTerm', searchTerm)
    if (companyFilter.length > 0) urlParams.set('companyFilter', companyFilter.join(','))
    window.history.pushState({}, '', `?${urlParams.toString()}`)
  }

  useEffect(() => {
    updateUrlParams()
    fetchData()
  }, [pagination, debouncedSearch, selectedPartners, partnerGroupFilter, companyFilter])

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedPartners([])
    setPartnerGroupFilter([])
    setCompanyFilter([])
    setPagination((prev) => ({ ...prev, pageNumber: 0 }))
  }

  const getPageNumbers = (currentPage, totalPages, maxButtons = 5) => {
    const pages = []
    const half = Math.floor(maxButtons / 2)
    let startPage = Math.max(0, currentPage - half)
    let endPage = Math.min(totalPages - 1, currentPage + half)
    if (currentPage - half < 0)
      endPage = Math.min(totalPages - 1, endPage + (half - currentPage))
    if (currentPage + half > totalPages - 1)
      startPage = Math.max(0, startPage - (currentPage + half - (totalPages - 1)))
    if (startPage > 0) {
      pages.push(0)
      if (startPage > 1) pages.push('ellipsis-left')
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i)
    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) pages.push('ellipsis-right')
      pages.push(totalPages - 1)
    }
    return pages
  }

  const renderTable = () => {
    if (loading) {
      return (
        <Box textAlign="center" p="4">
          <Spinner size="lg" />
        </Box>
      )
    }

    return (
      <Table
        variant="simple"
        size="md"
        sx={{ fontSize: 'sm', th: { p: 0.5 }, td: { p: 0.5 } }}
      >
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>
              <FilterMultiSelectPopover
                label="Companie WCR"
                options={organisations.map((o) => ({ value: o.name, label: o.name }))}
                selectedValues={companyFilter}
                onChange={(newVals) => {
                  setCompanyFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>

            <Th cursor="pointer">
              <Flex align="center">
                <span
                  onClick={() => handleSort('name')}
                  style={{ display: 'flex', alignItems: 'center', marginRight: '4px' }}
                >
                  Denumire parteneri
                  <Icon as={() => getSortIcon('name')} ml="2" />
                </span>
                {selectedPartners.length > 0 && (
                  <Text fontSize="sm" color="blue.500" mr="1">
                    ({selectedPartners.length})
                  </Text>
                )}
                <FilterPopoverWithCheck
                  label={LABEL.FILTER.label}
                  initialSelected={selectedPartners}
                  onSubmit={(vals) => {
                    setSelectedPartners(vals)
                    setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                  }}
                  fetchItems={getPartners}
                  displayField="name"
                  pageSize={10}
                  content={true}
                />
              </Flex>
            </Th>

            <Th cursor="pointer" onClick={() => handleSort('cif')}>
              <Flex align="center">
                CUI
                <Icon as={() => getSortIcon('cif')} ml="2" />
              </Flex>
            </Th>
            <Th>
              <FilterMultiSelectPopover
                label="Grupa partener"
                options={availablePartnerGroups.map((g) => ({ value: g, label: g }))}
                selectedValues={partnerGroupFilter}
                onChange={(newVals) => {
                  setPartnerGroupFilter(newVals)
                  setPagination((prev) => ({ ...prev, pageNumber: 0 }))
                }}
              />
            </Th>

            <Th cursor="pointer" onClick={() => handleSort('iban')}>
              <Flex align="center">
                IBAN
                <Icon as={() => getSortIcon('iban')} ml="2" />
              </Flex>
            </Th>

            <Th>Denumire Bancă</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <Tr key={row.id} _hover={{ backgroundColor: 'gray.100' }}>
                <Td borderRadius="md" border="1px solid" borderColor="gray.200">
                  {pagination.pageNumber * pagination.size + index + 1}
                </Td>
                <Td>{getWCRShortName(row.worldClassCif) || organisations.find((o) => o.cif === row.worldClassCif)?.name || row.worldClassCif || '-'}</Td>
                <Td>{row.name || '-'}</Td>
                <Td>{row.cif || '-'}</Td>
                <Td>{row.partnerGroup || '-'}</Td>
                <Td fontFamily="mono" fontSize="xs">
                  {row.iban || '-'}
                </Td>
                <Td>{row.bank || '-'}</Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan="7" textAlign="center">
                Nu s-au găsit parteneri.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    )
  }

  return (
    <>
      <Modal isOpen={isExporting} isCentered closeOnOverlayClick={false} closeOnEsc={false}>
        <ModalOverlay />
        <ModalContent maxW="280px" py={6}>
          <ModalBody>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text fontWeight="medium">Se descarcă exportul…</Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex align="center" justifyContent="space-between" width="100%" mb="5">
        <Flex align="center" gap={4} width="100%">
          <InputGroup flex="1">
            <InputLeftElement color="gray.500" pointerEvents="none">
              <FaSearch />
            </InputLeftElement>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPagination((prev) =>
                  prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }
                )
              }}
              placeholder="Caută după denumire, CUI sau IBAN…"
            />
          </InputGroup>

          <Button
            size="sm"
            colorScheme="red"
            onClick={clearAllFilters}
            leftIcon={<FaTrash />}
            flexShrink={0}
          >
            {LABEL.PROCESS_TABLE_HEAD.deleteFilters}
          </Button>

          <Button
            size="sm"
            colorScheme="green"
            onClick={handleExport}
            isLoading={isExporting}
            loadingText="Se exportă..."
            leftIcon={<FaFileExcel />}
            flexShrink={0}
          >
            Exportă
          </Button>
        </Flex>
      </Flex>

      {renderTable()}

      <Flex justify="space-between" mt="4" align="center">
        <Flex>
          <Select
            value={pagination.size}
            onChange={(e) =>
              setPagination((prev) => ({
                ...prev,
                size: Number(e.target.value),
                pageNumber: 0
              }))
            }
            width="fit-content"
          >
            {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((s) => (
              <option key={s} value={s}>
                {LABEL.DOCUMENTS_PER_PAGE.label(s)}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex direction="column" align="center">
          <Text mb={2}>
            Pagina {pagination.pageNumber + 1} / {totalPages}
          </Text>
          <Flex align="center" gap="2">
            <Button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageNumber: Math.max(prev.pageNumber - 1, 0)
                }))
              }
              isDisabled={pagination.pageNumber === 0}
              colorScheme="blue"
              variant="outline"
              size="sm"
              leftIcon={<FaChevronLeft />}
            >
              Precedent
            </Button>

            {getPageNumbers(pagination.pageNumber, totalPages).map((item) =>
              typeof item === 'number' ? (
                <Button
                  key={`page-${item}`}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, pageNumber: item }))
                  }
                  variant={pagination.pageNumber === item ? 'solid' : 'outline'}
                  size="sm"
                >
                  {item + 1}
                </Button>
              ) : (
                <Text key={item} px="2">
                  …
                </Text>
              )
            )}

            <Button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageNumber: Math.min(prev.pageNumber + 1, totalPages - 1)
                }))
              }
              isDisabled={pagination.pageNumber >= totalPages - 1}
              colorScheme="blue"
              variant="outline"
              size="sm"
              rightIcon={<Icon as={FaChevronRight} />}
            >
              Următor
            </Button>
          </Flex>

          <Flex mt="2" align="center" gap="2">
            <Text>Accesează pagina:</Text>
            <Input
              type="number"
              value={pageInput}
              onFocus={() => setPageInput('')}
              onChange={(e) => setPageInput(e.target.value)}
              onBlur={() => {
                if (pageInput === '')
                  setPageInput(String(pagination.pageNumber + 1))
              }}
              width="60px"
              min="1"
              max={totalPages}
              size="sm"
            />
            <Button size="sm" onClick={goToPage}>
              Aplică
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default PartnerIbanTable
